import React, { useState, useEffect } from 'react';
import { FiUpload, FiTrash2, FiCheck, FiImage } from 'react-icons/fi';
import { API_UPLOAD_URL, normalizeMediaUrl } from '../../config/apiConfig';
import '../ScrollStyles.css';

/**
 * Galeria de imagens EXCLUSIVA para packs
 * Armazena em subdiretório separado: /uploads/packs/
 */
export default function PackImageGallery({ 
  currentImage, 
  onImageSelect,
  cardTitle = "Imagem do Pack",
  cardHint = "Selecione ou faça upload de uma nova imagem"
}) {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    loadGallery();
  }, []);

  async function loadGallery() {
    setLoadingGallery(true);
    setError('');
    try {
      const apiUrl = API_UPLOAD_URL.replace('/upload.php', '/list-images.php');
      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data.success && data.data) {
        // Filtrar apenas imagens da pasta packs
        const packImages = data.data.filter(file => {
          const ext = file.filename.split('.').pop().toLowerCase();
          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
          // Verificar se está na pasta packs/ ou tem prefixo pack_
          const path = file.path || file.filename;
          const isPack = path.includes('packs/') || file.filename.includes('pack_') || file.url.includes('/packs/');
          return isImage && isPack;
        });
        setGalleryImages(packImages);
      } else {
        setError('Erro ao carregar galeria.');
      }
    } catch (err) {
      console.error('Erro ao carregar galeria:', err);
      setError('Erro de conexão ao carregar galeria.');
    } finally {
      setLoadingGallery(false);
    }
  }

  async function handleImageUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Validação de tipo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo não permitido. Use apenas JPG, PNG, GIF ou WEBP.');
      return;
    }

    // Validação de tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo: 5MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const formData = new FormData();
      
      // Renomear arquivo para incluir prefixo 'pack_'
      const timestamp = Date.now();
      const ext = file.name.split('.').pop();
      const newName = `pack_${timestamp}.${ext}`;
      const renamedFile = new File([file], newName, { type: file.type });
      
      formData.append('image', renamedFile);
      formData.append('folder', 'packs'); // Pasta específica
      
      const response = await fetch(API_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setUploadProgress(100);
        onImageSelect(data.data.url);
        loadGallery();
      } else {
        setError(data.message || 'Erro ao fazer upload da imagem.');
      }
    } catch (err) {
      console.error('Erro no upload:', err);
      setError('Erro de conexão com o servidor de upload.');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  }

  async function handleDeleteImage(filename, fileUrl) {
    if (!window.confirm(`Tem certeza que deseja excluir esta imagem?`)) {
      return;
    }

    try {
      const deleteUrl = API_UPLOAD_URL.replace('/upload.php', '/delete.php');
      const response = await fetch(deleteUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
      });

      const data = await response.json();

      if (data.success) {
        // Se a imagem deletada era a selecionada, limpar seleção
        if (currentImage === fileUrl) {
          onImageSelect('');
        }
        loadGallery();
      } else {
        setError(data.message || 'Erro ao excluir imagem.');
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
      setError('Erro ao excluir imagem.');
    }
  }

  return (
    <div style={styles.container}>
      {/* Header do Card */}
      <div style={styles.cardHeader}>
        <div style={styles.cardHeaderLeft}>
          <div style={styles.cardTitle}>{cardTitle}</div>
          <div style={styles.cardHint}>{cardHint}</div>
        </div>
        <div style={styles.cardHeaderRight}>
          <input
            type="file"
            id="pack-image-upload"
            accept="image/*"
            onChange={handleImageUpload}
            style={styles.fileInput}
            disabled={uploading}
          />
          <label htmlFor="pack-image-upload" style={styles.uploadButton}>
            <FiUpload size={14} />
            {uploading ? 'Enviando...' : 'Upload'}
          </label>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {uploading && (
        <div style={styles.progressBar}>
          <div style={{...styles.progressFill, width: `${uploadProgress}%`}}></div>
        </div>
      )}

      {/* Galeria */}
      <div style={styles.galleryContainer} className="scroll-container">
        {loadingGallery ? (
          <div style={styles.loading}>Carregando galeria...</div>
        ) : galleryImages.length === 0 ? (
          <div style={styles.empty}>
            <FiImage size={32} style={{ opacity: 0.3 }} />
            <p>Nenhuma imagem de pack disponível ainda.</p>
          </div>
        ) : (
          <div style={styles.gallery}>
            {galleryImages.map((image, index) => (
              <div
                key={index}
                style={{
                  ...styles.imageItem,
                  ...(currentImage === image.url ? styles.imageItemSelected : {})
                }}
                onClick={() => onImageSelect(normalizeMediaUrl(image.url))}
              >
                <img src={normalizeMediaUrl(image.url)} alt="Pack" style={styles.image} />
                
                {/* Badge de seleção */}
                {currentImage === image.url && (
                  <div style={styles.selectedBadge}>
                    <FiCheck size={16} />
                  </div>
                )}

                {/* Botão de deletar */}
                <button
                  className="delete-btn"
                  style={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Usar path se disponível, senão filename
                    const filePath = image.path || image.filename;
                    handleDeleteImage(filePath, image.url);
                  }}
                  title="Excluir imagem"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: '2px solid #e8e9ef',
  },
  cardHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#333',
  },
  cardHint: {
    fontSize: 12,
    color: '#666',
  },
  cardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
  },
  uploadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  fileInput: {
    display: 'none',
  },
  error: {
    padding: 10,
    background: '#ffe8e6',
    border: '1px solid #ffcbc4',
    borderRadius: 8,
    color: '#9a3c2f',
    fontSize: 13,
  },
  progressBar: {
    width: '100%',
    height: 4,
    background: '#e8e9ef',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#1f6feb',
    transition: 'width 0.3s ease',
  },
  galleryContainer: {
    maxHeight: 400,
    overflowY: 'auto',
    border: '1px solid #e8e9ef',
    borderRadius: 10,
    padding: 12,
  },
  loading: {
    textAlign: 'center',
    padding: 20,
    color: '#666',
  },
  empty: {
    textAlign: 'center',
    padding: 40,
    color: '#999',
  },
  gallery: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: 12,
  },
  imageItem: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: 10,
    overflow: 'hidden',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  imageItemSelected: {
    border: '2px solid #1f6feb',
    boxShadow: '0 0 0 4px rgba(31, 111, 235, 0.1)',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  selectedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    background: '#1f6feb',
    color: '#fff',
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  deleteButton: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s, transform 0.2s',
    zIndex: 3,
  },
};
